import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiLink, FiUploadCloud } from 'react-icons/fi';
import Swal from 'sweetalert2';

const API_URL = 'https://app.thundermaxhome.com/api.php';
const UPLOAD_URL = 'https://app.thundermaxhome.com/upload.php';

export default function WebsiteManagerPage() {
  const [activeTab, setActiveTab] = useState('products'); // products, portfolio, settings
  const [products, setProducts] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Website Settings Data
  const [webSettings, setWebSettings] = useState({
    show_hero: '1',
    show_stats: '1',
    show_services: '1',
    show_catalog: '1',
    show_portfolio: '1',
    show_booking: '1',
    show_footer: '1',
    hero_title: '',
    hero_subtitle: '',
    company_phone: '',
    company_email: ''
  });

  // States สำหรับ Modals
  const [showProductModal, setShowProductModal] = useState(false);
  const [showPortfolioModal, setShowPortfolioModal] = useState(false);
  const [productModalMode, setProductModalMode] = useState('add'); // 'add' or 'edit'
  const [portfolioModalMode, setPortfolioModalMode] = useState('add'); // 'add' or 'edit'

  const [uploading, setUploading] = useState(false);

  // Product Form Data
  const [productForm, setProductForm] = useState({
    id: '',
    model_name: '',
    brand: 'Daikin',
    btu: 9000,
    specs: '',
    price: 12000,
    stock: 10,
    image_url: '',
    affiliate_url: ''
  });

  // Portfolio Form Data
  const [portfolioForm, setPortfolioForm] = useState({
    id: '',
    title: '',
    description: '',
    category: 'ล้างแอร์',
    image_url: ''
  });

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      if (activeTab === 'products') {
        const res = await fetch(`${API_URL}?action=get_products`);
        const data = await res.json();
        if (data.success) setProducts(data.data || []);
      } else if (activeTab === 'portfolio') {
        const res = await fetch(`${API_URL}?action=get_portfolios`);
        const data = await res.json();
        if (data.success) setPortfolios(data.data || []);
      } else if (activeTab === 'settings') {
        const res = await fetch(`${API_URL}?action=get_website_settings`);
        const data = await res.json();
        if (data.success) setWebSettings(data.data || {});
      }
    } catch (err) {
      console.error(err);
      Swal.fire('ดึงข้อมูลล้มเหลว', 'ไม่สามารถเชื่อมต่อข้อมูลหลังบ้านได้', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    Swal.fire({ title: 'กำลังบันทึกตั้งค่า...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    try {
      const res = await fetch(`${API_URL}?action=update_website_settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(webSettings)
      });
      const data = await res.json();
      if (data.success) {
        Swal.fire({ icon: 'success', title: 'บันทึกการตั้งค่าแล้ว!', showConfirmButton: false, timer: 1500 });
      } else {
        throw new Error(data.message);
      }
    } catch (err) {
      Swal.fire('บันทึกล้มเหลว', err.message, 'error');
    }
  };

  // --- Image Upload Handler ---
  const handleImageUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    setUploading(true);
    try {
      const res = await fetch(UPLOAD_URL, {
        method: 'POST',
        body: formData
      });
      const data = await res.json();

      if (data.success) {
        if (type === 'product') {
          setProductForm(prev => ({ ...prev, image_url: data.url }));
        } else {
          setPortfolioForm(prev => ({ ...prev, image_url: data.url }));
        }
        Swal.fire({ icon: 'success', title: 'อัปโหลดสำเร็จ!', showConfirmButton: false, timer: 1000 });
      } else {
        throw new Error(data.message);
      }
    } catch (err) {
      Swal.fire('อัปโหลดล้มเหลว', err.message || 'เกิดข้อผิดพลาดในการส่งไฟล์', 'error');
    } finally {
      setUploading(false);
    }
  };

  // --- Product CRUD ---
  const handleOpenProductAdd = () => {
    setProductModalMode('add');
    setProductForm({
      id: '',
      model_name: '',
      brand: 'Daikin',
      btu: 9000,
      specs: 'Inverter, ประหยัดไฟเบอร์ 5, สารทำความเย็น R32',
      price: 12000,
      stock: 10,
      image_url: '',
      affiliate_url: ''
    });
    setShowProductModal(true);
  };

  const handleOpenProductEdit = (product) => {
    setProductModalMode('edit');
    setProductForm({
      id: product.id,
      model_name: product.model_name,
      brand: product.brand,
      btu: product.btu,
      specs: product.specs,
      price: product.price,
      stock: product.stock,
      image_url: product.image_url || '',
      affiliate_url: product.affiliate_url || ''
    });
    setShowProductModal(true);
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    const action = productModalMode === 'add' ? 'add_product' : 'edit_product';
    Swal.fire({ title: 'กำลังบันทึก...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

    try {
      const res = await fetch(`${API_URL}?action=${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(productForm)
      });
      const data = await res.json();

      if (data.success) {
        Swal.fire({ icon: 'success', title: 'บันทึกสำเร็จ!', showConfirmButton: false, timer: 1500 });
        setShowProductModal(false);
        fetchData();
      } else {
        throw new Error(data.message);
      }
    } catch (err) {
      Swal.fire('ล้มเหลว', err.message, 'error');
    }
  };

  const handleProductDelete = async (id, name) => {
    const confirm = await Swal.fire({
      title: 'ลบสินค้าหน้าเว็บ?',
      text: `คุณแน่ใจว่าต้องการลบสินค้า "${name}" ออกจากระบบหรือไม่`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      confirmButtonText: 'ลบออก',
      cancelButtonText: 'ยกเลิก'
    });

    if (confirm.isConfirmed) {
      try {
        const res = await fetch(`${API_URL}?action=delete_product`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id })
        });
        const data = await res.json();
        if (data.success) {
          Swal.fire('ลบสำเร็จ!', '', 'success');
          fetchData();
        }
      } catch (err) {
        Swal.fire('ล้มเหลว', 'เกิดข้อผิดพลาดในการลบสินค้า', 'error');
      }
    }
  };

  // --- Portfolio CRUD ---
  const handleOpenPortfolioAdd = () => {
    setPortfolioModalMode('add');
    setPortfolioForm({
      id: '',
      title: '',
      description: '',
      category: 'ล้างแอร์',
      image_url: ''
    });
    setShowPortfolioModal(true);
  };

  const handleOpenPortfolioEdit = (item) => {
    setPortfolioModalMode('edit');
    setPortfolioForm({
      id: item.id,
      title: item.title,
      description: item.description,
      category: item.category,
      image_url: item.image_url || ''
    });
    setShowPortfolioModal(true);
  };

  const handlePortfolioSubmit = async (e) => {
    e.preventDefault();
    const action = portfolioModalMode === 'add' ? 'add_portfolio' : 'edit_portfolio';
    Swal.fire({ title: 'กำลังบันทึกผลงาน...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

    try {
      const res = await fetch(`${API_URL}?action=${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(portfolioForm)
      });
      const data = await res.json();

      if (data.success) {
        Swal.fire({ icon: 'success', title: 'บันทึกผลงานสำเร็จ!', showConfirmButton: false, timer: 1500 });
        setShowPortfolioModal(false);
        fetchData();
      } else {
        throw new Error(data.message);
      }
    } catch (err) {
      Swal.fire('ล้มเหลว', err.message, 'error');
    }
  };

  const handlePortfolioDelete = async (id, title) => {
    const confirm = await Swal.fire({
      title: 'ต้องการลบผลงานช่าง?',
      text: `คุณแน่ใจว่าต้องการลบผลงาน "${title}" หรือไม่`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      confirmButtonText: 'ลบออก',
      cancelButtonText: 'ยกเลิก'
    });

    if (confirm.isConfirmed) {
      try {
        const res = await fetch(`${API_URL}?action=delete_portfolio`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id })
        });
        const data = await res.json();
        if (data.success) {
          Swal.fire('ลบสำเร็จ!', '', 'success');
          fetchData();
        }
      } catch (err) {
        Swal.fire('ล้มเหลว', 'เกิดข้อผิดพลาดในการลบผลงาน', 'error');
      }
    }
  };

  return (
    <div className="space-y-6 pb-10 text-gray-800 dark:text-gray-100 animate-fade-in text-left font-sans">
      <div>
        <h1 className="text-xl font-black text-gray-850 dark:text-white">🌐 ปรับแต่งเนื้อหาเว็บ (CMS Console)</h1>
        <p className="text-xs text-gray-505 dark:text-gray-400 mt-1">อัปเดตแคตตาล็อกสินค้าเครื่องปรับอากาศและข้อมูลประมวลภาพผลงานช่างปฏิบัติงานจริงบนเว็บไซต์หลัก</p>
      </div>

      {/* 🧭 Tabs */}
      <div className="flex border-b border-gray-250 dark:border-slate-800">
        <button
          onClick={() => setActiveTab('products')}
          className={`px-5 py-3 text-xs font-bold border-b-2 transition ${activeTab === 'products' ? 'border-indigo-500 text-indigo-500' : 'border-transparent text-gray-455 hover:text-gray-700 dark:hover:text-white'}`}
        >
          📦 สินค้าหน้าเว็บ (E-Catalog)
        </button>
        <button
          onClick={() => setActiveTab('portfolio')}
          className={`px-5 py-3 text-xs font-bold border-b-2 transition ${activeTab === 'portfolio' ? 'border-indigo-500 text-indigo-500' : 'border-transparent text-gray-455 hover:text-gray-700 dark:hover:text-white'}`}
        >
          📸 ข้อมูลผลงานช่าง (Portfolios)
        </button>
        <button
          onClick={() => setActiveTab('settings')}
          className={`px-5 py-3 text-xs font-bold border-b-2 transition ${activeTab === 'settings' ? 'border-indigo-500 text-indigo-500' : 'border-transparent text-gray-455 hover:text-gray-700 dark:hover:text-white'}`}
        >
          ⚙️ การแสดงผลหน้าเว็บ (Layout CMS)
        </button>
      </div>

      {/* Action Button */}
      <div className="flex justify-end">
        {activeTab === 'products' && (
          <button onClick={handleOpenProductAdd} className="bg-indigo-600 hover:bg-indigo-550 text-white px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-2 shadow-sm transition">
            <FiPlus /> เพิ่มสินค้า E-Catalog
          </button>
        )}
        {activeTab === 'portfolio' && (
          <button onClick={handleOpenPortfolioAdd} className="bg-indigo-600 hover:bg-indigo-550 text-white px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-2 shadow-sm transition">
            <FiPlus /> เพิ่มประวัติผลงานช่าง
          </button>
        )}
      </div>

      {/* 📊 Main Data View */}
      <div className="bg-white dark:bg-[#131825] rounded-2xl border border-gray-200 dark:border-[#1e2330] overflow-hidden shadow-sm transition-colors">
        {isLoading ? (
          <div className="text-center py-20 font-bold text-indigo-500 animate-pulse text-xs">กำลังโหลดข้อมูลหลัก...</div>
        ) : activeTab === 'products' ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead className="bg-gray-50 dark:bg-[#1b2132]/40 text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-slate-800">
                <tr>
                  <th className="px-6 py-4 font-bold" style={{ width: '15%' }}>ภาพสินค้า</th>
                  <th className="px-6 py-4 font-bold" style={{ width: '35%' }}>รุ่นแอร์ / จุดเด่น</th>
                  <th className="px-6 py-4 font-bold text-right" style={{ width: '15%' }}>ราคาจำหน่าย</th>
                  <th className="px-6 py-4 font-bold" style={{ width: '20%' }}>ลิงก์จำหน่าย (Affiliate)</th>
                  <th className="px-6 py-4 font-bold text-center" style={{ width: '15%' }}>จัดการ</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                {products.map(p => (
                  <tr key={p.id} className="hover:bg-slate-50/50 dark:hover:bg-[#1b2132]/10 transition-colors">
                    <td className="px-6 py-4">
                      {p.image_url ? (
                        <img src={p.image_url} alt={p.model_name} className="w-16 h-10 object-cover rounded border border-gray-205 dark:border-slate-700 bg-slate-900" />
                      ) : (
                        <div className="w-16 h-10 bg-gray-100 dark:bg-slate-900 rounded flex items-center justify-center text-[10px] text-gray-400 border border-gray-200 dark:border-slate-700">ไม่มีรูป</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-bold text-gray-800 dark:text-white">{p.model_name}</div>
                      <div className="text-[10px] text-gray-450 dark:text-gray-500 mt-1">{p.brand} | {Number(p.btu).toLocaleString()} BTU</div>
                    </td>
                    <td className="px-6 py-4 text-right font-black text-emerald-500">
                      ฿{Number(p.price).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 max-w-[200px] truncate">
                      {p.affiliate_url ? (
                        <a href={p.affiliate_url} target="_blank" rel="noreferrer" className="text-indigo-500 hover:text-indigo-400 flex items-center gap-1 font-bold">
                          <FiLink size={12} /> Shopee/Lazada Link
                        </a>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-550 text-[10px]">ไม่ได้ระบุลิงก์</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center gap-3">
                        <button onClick={() => handleOpenProductEdit(p)} className="text-gray-400 hover:text-indigo-500 transition"><FiEdit2 size={14} /></button>
                        <button onClick={() => handleProductDelete(p.id, p.model_name)} className="text-gray-400 hover:text-red-500 transition"><FiTrash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : activeTab === 'portfolio' ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead className="bg-gray-50 dark:bg-[#1b2132]/40 text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-slate-800">
                <tr>
                  <th className="px-6 py-4 font-bold" style={{ width: '15%' }}>ภาพผลงาน</th>
                  <th className="px-6 py-4 font-bold" style={{ width: '20%' }}>หมวดหมู่</th>
                  <th className="px-6 py-4 font-bold" style={{ width: '45%' }}>หัวข้อ & คำอธิบาย</th>
                  <th className="px-6 py-4 font-bold text-center" style={{ width: '20%' }}>จัดการ</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                {portfolios.map(item => (
                  <tr key={item.id} className="hover:bg-slate-50/50 dark:hover:bg-[#1b2132]/10 transition-colors">
                    <td className="px-6 py-4">
                      {item.image_url ? (
                        <img src={item.image_url} alt={item.title} className="w-16 h-12 object-cover rounded border border-gray-205 dark:border-slate-700 bg-slate-900" />
                      ) : (
                        <div className="w-16 h-12 bg-gray-100 dark:bg-slate-900 rounded flex items-center justify-center text-[10px] text-gray-400 border border-gray-200 dark:border-slate-700">ไม่มีรูป</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="bg-indigo-500/10 text-indigo-500 dark:text-indigo-400 border border-indigo-500/20 px-2.5 py-0.5 rounded text-[10px] font-bold">
                        {item.category}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-bold text-gray-800 dark:text-white">{item.title}</div>
                      <div className="text-[10px] text-gray-450 dark:text-gray-500 mt-1 max-w-[400px] truncate">{item.description}</div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center gap-3">
                        <button onClick={() => handleOpenPortfolioEdit(item)} className="text-gray-400 hover:text-indigo-500 transition"><FiEdit2 size={14} /></button>
                        <button onClick={() => handlePortfolioDelete(item.id, item.title)} className="text-gray-400 hover:text-red-500 transition"><FiTrash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <form onSubmit={handleSaveSettings} className="p-8 space-y-6 text-xs text-left">
            <h2 className="text-sm font-bold text-gray-800 dark:text-white border-b pb-2 border-gray-150 dark:border-slate-800">
              🛠️ ส่วนแสดงผลหน้าเว็บหลัก (Show / Hide Columns)
            </h2>
            <p className="text-[10px] text-gray-400 -mt-2">เลือกสลับเพื่อแสดงหรือซ่อนส่วนต่างๆ บนหน้าเว็บหลัก (www.thundermaxhome.com) ได้ทันที</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="flex items-center justify-between p-3.5 bg-gray-50 dark:bg-slate-800/40 rounded-xl border border-gray-100 dark:border-slate-800">
                <div>
                  <div className="font-bold text-gray-800 dark:text-white">🚀 Hero Banner (ส่วนบนสุด)</div>
                  <div className="text-[10px] text-gray-400 mt-0.5">ส่วนโปรโมทหลักบนสุดของหน้าเว็บ</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={webSettings.show_hero === '1'} 
                    onChange={e => setWebSettings({...webSettings, show_hero: e.target.checked ? '1' : '0'})} 
                    className="sr-only peer" 
                  />
                  <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-3.5 bg-gray-50 dark:bg-slate-800/40 rounded-xl border border-gray-100 dark:border-slate-800">
                <div>
                  <div className="font-bold text-gray-800 dark:text-white">📊 Stats Section (สถิติผลงาน)</div>
                  <div className="text-[10px] text-gray-400 mt-0.5">แถบแสดงจำนวนงานสำเร็จและระดับคะแนน</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={webSettings.show_stats === '1'} 
                    onChange={e => setWebSettings({...webSettings, show_stats: e.target.checked ? '1' : '0'})} 
                    className="sr-only peer" 
                  />
                  <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-3.5 bg-gray-50 dark:bg-slate-800/40 rounded-xl border border-gray-100 dark:border-slate-800">
                <div>
                  <div className="font-bold text-gray-800 dark:text-white">⚙️ Services Grid (คอลัมน์บริการ)</div>
                  <div className="text-[10px] text-gray-400 mt-0.5">รายละเอียดบริการล้าง ติดตั้ง ซ่อมแซมแอร์</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={webSettings.show_services === '1'} 
                    onChange={e => setWebSettings({...webSettings, show_services: e.target.checked ? '1' : '0'})} 
                    className="sr-only peer" 
                  />
                  <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-3.5 bg-gray-50 dark:bg-slate-800/40 rounded-xl border border-gray-100 dark:border-slate-800">
                <div>
                  <div className="font-bold text-gray-800 dark:text-white">📦 Catalog E-Shop (รายการสินค้า)</div>
                  <div className="text-[10px] text-gray-400 mt-0.5">ตารางและรายการแอร์บ้านราคาพิเศษ</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={webSettings.show_catalog === '1'} 
                    onChange={e => setWebSettings({...webSettings, show_catalog: e.target.checked ? '1' : '0'})} 
                    className="sr-only peer" 
                  />
                  <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-3.5 bg-gray-50 dark:bg-slate-800/40 rounded-xl border border-gray-100 dark:border-slate-800">
                <div>
                  <div className="font-bold text-gray-800 dark:text-white">📸 Portfolio Gallery (รูปผลงาน)</div>
                  <div className="text-[10px] text-gray-400 mt-0.5">แสดงผลงานช่างแบบ 3 คอลัมน์ที่ตั้งไว้</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={webSettings.show_portfolio === '1'} 
                    onChange={e => setWebSettings({...webSettings, show_portfolio: e.target.checked ? '1' : '0'})} 
                    className="sr-only peer" 
                  />
                  <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-3.5 bg-gray-50 dark:bg-slate-800/40 rounded-xl border border-gray-100 dark:border-slate-800">
                <div>
                  <div className="font-bold text-gray-800 dark:text-white">📅 Calendar Booking (ปฏิทินจอง)</div>
                  <div className="text-[10px] text-gray-400 mt-0.5">ฟอร์มและระบบจองคิวออนไลน์ 24 ชม.</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={webSettings.show_booking === '1'} 
                    onChange={e => setWebSettings({...webSettings, show_booking: e.target.checked ? '1' : '0'})} 
                    className="sr-only peer" 
                  />
                  <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                </label>
              </div>
            </div>

            <h2 className="text-sm font-bold text-gray-800 dark:text-white border-b pb-2 border-gray-150 dark:border-slate-800 mt-8">
              ✏️ แก้ไขเนื้อหาข้อความบนหน้าเว็บ (Web Content Customizer)
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="font-bold text-gray-700 dark:text-gray-300">หัวข้อหลักของแบนเนอร์ (Hero Title)</label>
                <input 
                  type="text" 
                  required 
                  value={webSettings.hero_title || ''} 
                  onChange={e => setWebSettings({...webSettings, hero_title: e.target.value})} 
                  className="w-full bg-gray-50 dark:bg-[#0c0c0e] border border-gray-250 dark:border-gray-700 rounded-xl p-2.5 text-xs text-gray-800 dark:text-white mt-1 outline-none" 
                />
              </div>

              <div>
                <label className="font-bold text-gray-700 dark:text-gray-300">หัวข้อย่อยคำอธิบายแบนเนอร์ (Hero Subtitle)</label>
                <input 
                  type="text" 
                  required 
                  value={webSettings.hero_subtitle || ''} 
                  onChange={e => setWebSettings({...webSettings, hero_subtitle: e.target.value})} 
                  className="w-full bg-gray-50 dark:bg-[#0c0c0e] border border-gray-250 dark:border-gray-700 rounded-xl p-2.5 text-xs text-gray-800 dark:text-white mt-1 outline-none" 
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="font-bold text-gray-700 dark:text-gray-300">เบอร์โทรศัพท์ติดต่อจริง (Company Phone)</label>
                  <input 
                    type="text" 
                    required 
                    value={webSettings.company_phone || ''} 
                    onChange={e => setWebSettings({...webSettings, company_phone: e.target.value})} 
                    className="w-full bg-gray-55 dark:bg-[#0c0c0e] border border-gray-250 dark:border-gray-700 rounded-xl p-2.5 text-xs text-gray-800 dark:text-white mt-1 outline-none" 
                  />
                </div>
                <div>
                  <label className="font-bold text-gray-700 dark:text-gray-300">อีเมลติดต่อบริษัท (Company Email)</label>
                  <input 
                    type="email" 
                    required 
                    value={webSettings.company_email || ''} 
                    onChange={e => setWebSettings({...webSettings, company_email: e.target.value})} 
                    className="w-full bg-gray-55 dark:bg-[#0c0c0e] border border-gray-250 dark:border-gray-700 rounded-xl p-2.5 text-xs text-gray-800 dark:text-white mt-1 outline-none" 
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <button type="submit" className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold px-6 py-3 rounded-xl shadow-sm transition flex items-center gap-2">
                <FiSave /> บันทึกการตั้งค่าเว็บทั้งหมด
              </button>
            </div>
          </form>
        )}
      </div>

      {/* 📦 Product Form Modal */}
      {showProductModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#131825] w-full max-w-lg rounded-2xl shadow-xl border border-gray-200 dark:border-slate-800 overflow-hidden">
            <div className="p-5 border-b border-gray-100 dark:border-slate-800 flex justify-between items-center bg-gray-50 dark:bg-[#1b2132]/30">
              <h3 className="font-bold text-xs text-gray-850 dark:text-white">
                {productModalMode === 'add' ? '➕ เพิ่มสินค้า E-Catalog' : '✏️ แก้ไขข้อมูลสินค้าหน้าเว็บ'}
              </h3>
              <button onClick={() => setShowProductModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-white transition"><FiX size={20} /></button>
            </div>

            <form onSubmit={handleProductSubmit} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto text-xs text-left">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-bold text-gray-700 dark:text-gray-300">แบรนด์สินค้า</label>
                  <select value={productForm.brand} onChange={e => setProductForm({...productForm, brand: e.target.value})} className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-250 dark:border-gray-700 rounded-xl p-2.5 text-xs text-gray-800 dark:text-white mt-1 outline-none">
                    <option value="Daikin">Daikin</option>
                    <option value="Mitsubishi">Mitsubishi</option>
                    <option value="Carrier">Carrier</option>
                    <option value="Panasonic">Panasonic</option>
                    <option value="Haier">Haier</option>
                  </select>
                </div>
                <div>
                  <label className="font-bold text-gray-700 dark:text-gray-300">ขนาด BTU</label>
                  <input type="number" required value={productForm.btu} onChange={e => setProductForm({...productForm, btu: Number(e.target.value)})} className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-250 dark:border-gray-700 rounded-xl p-2.5 text-xs text-gray-800 dark:text-white mt-1 outline-none" />
                </div>
              </div>

              <div>
                <label className="font-bold text-gray-700 dark:text-gray-300">ชื่อรุ่นสินค้า</label>
                <input type="text" required value={productForm.model_name} onChange={e => setProductForm({...productForm, model_name: e.target.value})} className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-250 dark:border-gray-700 rounded-xl p-2.5 text-xs text-gray-800 dark:text-white mt-1 outline-none" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-bold text-gray-700 dark:text-gray-300">ราคาขาย (บาท)</label>
                  <input type="number" required value={productForm.price} onChange={e => setProductForm({...productForm, price: Number(e.target.value)})} className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-250 dark:border-gray-700 rounded-xl p-2.5 text-xs text-gray-800 dark:text-white mt-1 outline-none" />
                </div>
                <div>
                  <label className="font-bold text-gray-700 dark:text-gray-300">สต๊อกจำหน่าย</label>
                  <input type="number" required value={productForm.stock} onChange={e => setProductForm({...productForm, stock: Number(e.target.value)})} className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-250 dark:border-gray-700 rounded-xl p-2.5 text-xs text-gray-800 dark:text-white mt-1 outline-none" />
                </div>
              </div>

              <div>
                <label className="font-bold text-gray-700 dark:text-gray-300">ลิงก์ Affiliate (Shopee/Lazada หรือเว็บขายของ)</label>
                <input type="url" placeholder="https://shopee.co.th/..." value={productForm.affiliate_url} onChange={e => setProductForm({...productForm, affiliate_url: e.target.value})} className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-250 dark:border-gray-700 rounded-xl p-2.5 text-xs text-gray-800 dark:text-white mt-1 outline-none" />
              </div>

              <div>
                <label className="font-bold text-gray-700 dark:text-gray-300">สเปกและคำบรรยายเด่น</label>
                <textarea rows="2" value={productForm.specs} onChange={e => setProductForm({...productForm, specs: e.target.value})} className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-250 dark:border-gray-700 rounded-xl p-2.5 text-xs text-gray-800 dark:text-white mt-1 outline-none" />
              </div>

              {/* 🖼️ Image Upload & URL input */}
              <div>
                <label className="font-bold text-gray-700 dark:text-gray-300">รูปภาพสินค้า</label>
                <div className="flex gap-4 items-center mt-2">
                  {productForm.image_url ? (
                    <img src={productForm.image_url} alt="product-preview" className="w-20 h-14 object-cover rounded border border-gray-205 dark:border-slate-700 bg-slate-900" />
                  ) : (
                    <div className="w-20 h-14 bg-gray-100 dark:bg-slate-900 rounded border border-dashed border-gray-300 dark:border-slate-700 flex items-center justify-center text-[10px] text-gray-400">ไม่มีรูป</div>
                  )}
                  <label className="flex-1 flex flex-col items-center justify-center py-2 px-3 border border-dashed border-indigo-500 rounded-xl bg-indigo-500/5 hover:bg-indigo-500/10 cursor-pointer text-indigo-500 dark:text-indigo-400 font-bold text-xs transition">
                    <FiUploadCloud size={18} className="mb-1" />
                    <span>{uploading ? 'กำลังอัปโหลด...' : 'เลือกรูปภาพอัปโหลด'}</span>
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, 'product')} />
                  </label>
                </div>
                <div className="mt-2.5">
                  <input 
                    type="text" 
                    value={productForm.image_url} 
                    onChange={e => setProductForm({...productForm, image_url: e.target.value})} 
                    placeholder="หรือวางลิงก์รูปภาพโดยตรง (เช่น Google Drive, Web URL)" 
                    className="w-full bg-gray-55 dark:bg-gray-800 border border-gray-250 dark:border-gray-700 rounded-xl p-2.5 text-xs text-gray-800 dark:text-white outline-none font-mono" 
                  />
                </div>
              </div>

              <button type="submit" disabled={uploading} className="w-full bg-indigo-600 hover:bg-indigo-550 text-white font-bold py-3 rounded-xl shadow-sm transition flex justify-center items-center gap-2 mt-4 disabled:opacity-55">
                <FiSave /> บันทึกสินค้า
              </button>
            </form>
          </div>
        </div>
      )}

      {/* 📸 Portfolio Form Modal */}
      {showPortfolioModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#131825] w-full max-w-lg rounded-2xl shadow-xl border border-gray-200 dark:border-slate-800 overflow-hidden">
            <div className="p-5 border-b border-gray-100 dark:border-slate-800 flex justify-between items-center bg-gray-50 dark:bg-[#1b2132]/30">
              <h3 className="font-bold text-xs text-gray-850 dark:text-white">
                {portfolioModalMode === 'add' ? '➕ เพิ่มผลงานช่าง' : '✏️ แก้ไขข้อมูลผลงานช่าง'}
              </h3>
              <button onClick={() => setShowPortfolioModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-white transition"><FiX size={20} /></button>
            </div>

            <form onSubmit={handlePortfolioSubmit} className="p-6 space-y-4 text-xs text-left">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-bold text-gray-700 dark:text-gray-300">หมวดหมู่ผลงาน</label>
                  <select value={portfolioForm.category} onChange={e => setPortfolioForm({...portfolioForm, category: e.target.value})} className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-250 dark:border-gray-700 rounded-xl p-2.5 text-xs text-gray-800 dark:text-white mt-1 outline-none">
                    <option value="ล้างแอร์">ล้างแอร์</option>
                    <option value="ติดตั้งแอร์">ติดตั้งแอร์</option>
                    <option value="ซ่อมแอร์">ซ่อมแอร์</option>
                    <option value="งานไฟฟ้าระบบ">งานไฟฟ้าระบบ</option>
                  </select>
                </div>
                <div>
                  <label className="font-bold text-gray-700 dark:text-gray-300">หัวข้อผลงาน</label>
                  <input type="text" required value={portfolioForm.title} onChange={e => setPortfolioForm({...portfolioForm, title: e.target.value})} className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-250 dark:border-gray-700 rounded-xl p-2.5 text-xs text-gray-800 dark:text-white mt-1 outline-none" placeholder="เช่น ล้างแอร์บ้านเดี่ยวสองชั้น" />
                </div>
              </div>

              <div>
                <label className="font-bold text-gray-700 dark:text-gray-300">คำอธิบายงาน / รีวิวลูกค้า</label>
                <textarea rows="4" required value={portfolioForm.description} onChange={e => setPortfolioForm({...portfolioForm, description: e.target.value})} className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-250 dark:border-gray-700 rounded-xl p-2.5 text-xs text-gray-800 dark:text-white mt-1 outline-none" placeholder="รายละเอียดงานช่างหรือผลการปฏิบัติงานจริง..." />
              </div>

              {/* 🖼️ Image Upload & URL input */}
              <div>
                <label className="font-bold text-gray-700 dark:text-gray-300">รูปภาพผลงานช่าง</label>
                <div className="flex gap-4 items-center mt-2">
                  {portfolioForm.image_url ? (
                    <img src={portfolioForm.image_url} alt="portfolio-preview" className="w-20 h-16 object-cover rounded border border-gray-205 dark:border-slate-700 bg-slate-900" />
                  ) : (
                    <div className="w-20 h-16 bg-gray-100 dark:bg-slate-900 rounded border border-dashed border-gray-300 dark:border-slate-700 flex items-center justify-center text-[10px] text-gray-400">ไม่มีรูป</div>
                  )}
                  <label className="flex-1 flex flex-col items-center justify-center py-3 px-3 border border-dashed border-indigo-500 rounded-xl bg-indigo-500/5 hover:bg-indigo-500/10 cursor-pointer text-indigo-500 dark:text-indigo-400 font-bold text-xs transition">
                    <FiUploadCloud size={20} className="mb-1" />
                    <span>{uploading ? 'กำลังอัปโหลด...' : 'เลือกรูปถ่ายผลงาน'}</span>
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, 'portfolio')} />
                  </label>
                </div>
                <div className="mt-2.5">
                  <input 
                    type="text" 
                    value={portfolioForm.image_url} 
                    onChange={e => setPortfolioForm({...portfolioForm, image_url: e.target.value})} 
                    placeholder="หรือวางลิงก์รูปภาพโดยตรง (เช่น Google Drive, Web URL)" 
                    className="w-full bg-gray-55 dark:bg-gray-800 border border-gray-250 dark:border-gray-700 rounded-xl p-2.5 text-xs text-gray-800 dark:text-white outline-none font-mono" 
                  />
                </div>
              </div>

              <button type="submit" disabled={uploading} className="w-full bg-indigo-600 hover:bg-indigo-550 text-white font-bold py-3 rounded-xl shadow-sm transition flex justify-center items-center gap-2 mt-6 disabled:opacity-55">
                <FiSave /> บันทึกข้อมูลผลงาน
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
